/**
 * Transaction Logger — captures all checkout steps and sends to backend + Sentry.
 *
 * Usage:
 *   const logger = createTransactionLogger('checkout', walletAddress);
 *   logger.step('UVM approval check', { current: '0', required: '2.06' });
 *   logger.step('UVM approval tx', { txHash: '0x...' });
 *   // on success:
 *   await logger.complete({ txHash, orderIds });
 *   // on failure:
 *   await logger.fail(error, { lastStep: 'buyNft' });
 */

type StepEntry = {
  step: string;
  status: "started" | "completed" | "failed";
  timestamp: number;
  duration_ms?: number;
  data?: Record<string, unknown>;
  error?: string;
};

type ErrorCategory =
  | "user_rejected"
  | "insufficient_funds"
  | "contract_revert"
  | "rpc_error"
  | "network_error"
  | "gas_error"
  | "allowance_error"
  | "unknown";

export function categorizeError(error: unknown): ErrorCategory {
  if (!error || !(error instanceof Error)) return "unknown";

  const msg = error.message.toLowerCase();
  const name = error.name?.toLowerCase() ?? "";

  if (
    msg.includes("user rejected") ||
    msg.includes("user denied") ||
    msg.includes("rejected the request") ||
    name.includes("userrejectedrequesterror")
  )
    return "user_rejected";

  // name check comes first: the thrown message is localized (fa/ar/en) via
  // InsufficientBalanceError, so the English substring check below only ever
  // catches the English locale — it stays as a fallback for older errors.
  if (
    name.includes("insufficientbalanceerror") ||
    msg.includes("insufficient funds") ||
    msg.includes("insufficient balance")
  )
    return "insufficient_funds";

  if (
    msg.includes("reverted") ||
    msg.includes("execution reverted") ||
    name.includes("contractfunctionreverted")
  )
    return "contract_revert";

  if (
    msg.includes("gas") ||
    msg.includes("intrinsic gas") ||
    name.includes("estimategasexecutionerror")
  )
    return "gas_error";

  if (msg.includes("allowance") || msg.includes("approval"))
    return "allowance_error";

  if (
    msg.includes("network") ||
    msg.includes("timeout") ||
    msg.includes("failed to fetch") ||
    msg.includes("disconnected")
  )
    return "network_error";

  if (
    msg.includes("rpc") ||
    msg.includes("provider") ||
    msg.includes("internal json-rpc")
  )
    return "rpc_error";

  return "unknown";
}

function serializeError(error: unknown): string {
  if (!error) return "unknown error";
  if (error instanceof Error) {
    const parts = [error.name, error.message];
    const cause = (error as Error & { cause?: unknown }).cause;
    if (cause instanceof Error) {
      parts.push(`cause: ${cause.name}: ${cause.message}`);
    }
    return parts.join(": ");
  }
  return String(error);
}

export function createTransactionLogger(
  type: "checkout" | "nft-purchase",
  walletAddress: string,
) {
  const steps: StepEntry[] = [];
  const startedAt = Date.now();
  let currentStep: { name: string; start: number } | null = null;

  function finishCurrentStep(status: "completed" | "failed" = "completed") {
    if (currentStep) {
      const existing = steps.find(
        (s) => s.step === currentStep?.name && s.status === "started",
      );
      if (existing) {
        existing.status = status;
        existing.duration_ms = Date.now() - currentStep.start;
      }
      currentStep = null;
    }
  }

  function step(name: string, data?: Record<string, unknown>) {
    finishCurrentStep("completed");
    currentStep = { name, start: Date.now() };
    steps.push({
      step: name,
      status: "started",
      timestamp: Date.now(),
      data,
    });
  }

  function stepFailed(name: string, error: unknown) {
    finishCurrentStep("failed");
    const last = steps.find((s) => s.step === name && s.status === "started");
    if (last) {
      last.status = "failed";
      last.error = serializeError(error);
      last.duration_ms = Date.now() - (currentStep?.start ?? Date.now());
    } else {
      steps.push({
        step: name,
        status: "failed",
        timestamp: Date.now(),
        error: serializeError(error),
      });
    }
    currentStep = null;
  }

  async function send(
    status: "success" | "failed",
    metadata?: Record<string, unknown>,
    error?: unknown,
  ) {
    finishCurrentStep(status === "success" ? "completed" : "failed");

    const completedAt = Date.now();
    const payload = {
      wallet_address: walletAddress,
      type,
      status,
      error_category: error ? categorizeError(error) : undefined,
      error_message: error ? serializeError(error) : undefined,
      steps,
      metadata,
      duration_ms: completedAt - startedAt,
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      started_at: new Date(startedAt).toISOString(),
      completed_at: new Date(completedAt).toISOString(),
    };

    // Send to backend (fire-and-forget)
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? "";
      fetch(`${apiUrl}/order/transaction-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch {
      // never block the purchase flow
    }

    // Send to Sentry with full context (production only)
    if (status === "failed" && error) {
      try {
        const Sentry = await import("@sentry/nextjs");
        Sentry.withScope((scope) => {
          scope.setTag("transaction_type", type);
          scope.setTag("error_category", payload.error_category ?? "unknown");
          scope.setContext("transaction_log", {
            wallet: walletAddress,
            duration_ms: payload.duration_ms,
            step_count: steps.length,
            last_step: steps[steps.length - 1]?.step,
          });
          scope.setContext(
            "steps",
            Object.fromEntries(
              steps.map((s, i) => [
                `${i}_${s.step}`,
                {
                  status: s.status,
                  duration_ms: s.duration_ms,
                  error: s.error,
                  data: s.data,
                },
              ]),
            ),
          );
          if (metadata) {
            scope.setContext("metadata", metadata as Record<string, unknown>);
          }
          const err =
            error instanceof Error ? error : new Error(serializeError(error));
          Sentry.captureException(err);
        });
      } catch {
        // Sentry may not be loaded in dev
      }
    }
  }

  return {
    step,
    stepFailed,
    async complete(metadata?: Record<string, unknown>) {
      await send("success", metadata);
    },
    async fail(error: unknown, metadata?: Record<string, unknown>) {
      await send("failed", metadata, error);
    },
    getSteps() {
      return [...steps];
    },
  };
}
